import { RENEWAL_MODES } from '@oefy/core';
import { z } from 'zod';
import { timestampSchema } from './common';

export const KEY_SOURCES = ['stored', 'environment', 'missing'] as const;

export const keySourceSchema = z.enum(KEY_SOURCES);

export const webshopNameSchema = z.string().trim().min(1).max(80);

export const webshopUrlSchema = z
  .string()
  .trim()
  .pipe(z.url({ protocol: /^https$/, error: 'Use the https address of the shop' }));

export const consumerKeySchema = z
  .string()
  .trim()
  .regex(/^ck_[A-Za-z0-9]{20,64}$/, 'A WooCommerce key starts with ck_');

export const consumerSecretSchema = z
  .string()
  .trim()
  .regex(/^cs_[A-Za-z0-9]{20,64}$/, 'A WooCommerce secret starts with cs_');

export const renewalModeSchema = z.enum(RENEWAL_MODES);

export const mollieKeySchema = z
  .string()
  .trim()
  .regex(/^(live|test)_[A-Za-z0-9]{20,64}$/, 'A Mollie API key starts with live_ or test_');

export const webshopSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  url: z.string().nullable(),
  legacy: z.boolean(),
  active: z.boolean(),
  shipsBooks: z.boolean(),
  renewal: renewalModeSchema,
  manageUrl: z.string().nullable(),
  mollie: z.object({ name: z.string(), keyHint: z.string(), live: z.boolean() }).nullable(),
  keys: keySourceSchema,
  keyHint: z.string().nullable(),
  webhookSecret: keySourceSchema,
  webhookUrl: z.string(),
  checkedAt: timestampSchema.nullable(),
  checkError: z.string().nullable(),
  lastOrderAt: timestampSchema.nullable(),
  renewalConfirmed: z.boolean(),
  webhookPingAt: timestampSchema.nullable(),
  lastWebhookAt: timestampSchema.nullable(),
  orderViaWebhook: z.boolean(),
  missingWebhook: z.boolean(),
  stats: z.object({
    families: z.int().min(0),
    active: z.int().min(0),
    renewing: z.int().min(0),
    failed: z.int().min(0),
    cancelled: z.int().min(0),
  }),
  activeSubscriptions: z.int().min(0),
  activeFamilies: z.int().min(0),
  heldEvents: z.int().min(0),
  deletable: z.boolean(),
  createdAt: timestampSchema,
});

export const webshopListSchema = z.object({
  shops: z.array(webshopSchema),
  canStoreKeys: z.boolean(),
  canManage: z.boolean(),
});

export const webshopCreateSchema = z.object({
  name: webshopNameSchema,
  url: webshopUrlSchema,
  consumerKey: consumerKeySchema,
  consumerSecret: consumerSecretSchema,
});

export const webshopUpdateSchema = z
  .object({
    name: webshopNameSchema.optional(),
    url: webshopUrlSchema.optional(),
    active: z.boolean().optional(),
    shipsBooks: z.literal(true).optional(),
    renewal: renewalModeSchema.optional(),
    renewalConfirmed: z.literal(true).optional(),
    manageUrl: webshopUrlSchema.nullable().optional(),
  })
  .refine((input) => Object.keys(input).length > 0, 'Change at least one field');

export const webshopKeysSchema = z.object({
  consumerKey: consumerKeySchema,
  consumerSecret: consumerSecretSchema,
});

export const webshopMollieSchema = z.object({ apiKey: mollieKeySchema });

export const webshopSecretSchema = z.object({ secret: z.string() });

export const webshopCheckSchema = z.object({
  ok: z.boolean(),
  error: z.string().nullable(),
  checkedAt: timestampSchema,
});

export type KeySource = z.infer<typeof keySourceSchema>;
export type Webshop = z.infer<typeof webshopSchema>;
export type WebshopList = z.infer<typeof webshopListSchema>;
export type WebshopCreateInput = z.input<typeof webshopCreateSchema>;
export type WebshopCreate = z.output<typeof webshopCreateSchema>;
export type WebshopUpdateInput = z.input<typeof webshopUpdateSchema>;
export type WebshopUpdate = z.output<typeof webshopUpdateSchema>;
export type WebshopKeysInput = z.input<typeof webshopKeysSchema>;
export type WebshopKeys = z.output<typeof webshopKeysSchema>;
export type WebshopSecret = z.infer<typeof webshopSecretSchema>;
export type WebshopMollieInput = z.input<typeof webshopMollieSchema>;
export type WebshopMollie = z.output<typeof webshopMollieSchema>;
export type WebshopCheck = z.infer<typeof webshopCheckSchema>;
